import React, { useMemo, useState } from "react";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    PieChart, Pie, Cell,
    LineChart, Line,
    ComposedChart
} from "recharts";
import { parseToGB, normalizeStatus } from "./utils";

const PIE_COLORS = { active: "#10b981", inactive: "#f43f5e", servers: "#6366f1" };

const DashboardCharts = ({ servers, flatUsers, onShowGroup }) => {
    const [serverFilter, setServerFilter] = useState("all");
    const [providerFilter, setProviderFilter] = useState("all");
    const [monthFilter, setMonthFilter] = useState("all");

    // Get current user from localStorage
    const currentUser = useMemo(() => {
        try {
            // Try to get from 'employee' key first, fallback to 'user'
            let userData = localStorage.getItem('employee');
            if (!userData) {
                userData = localStorage.getItem('user');
            }
            return JSON.parse(userData || '{}');
        } catch {
            return {};
        }
    }, []);
    const isAdmin = currentUser.privilages === 2;

    const providerNames = useMemo(
        () => [...new Set(servers.map((s) => s.provider_name).filter(Boolean))],
        [servers]
    );

    const monthOptions = useMemo(() => {
        const set = new Set();
        flatUsers.forEach((u) => {
            if (u.activation_date) set.add(String(u.activation_date).slice(0, 7));
        });
        return [...set].sort();
    }, [flatUsers]);

    const filteredServers = useMemo(() => {
        return servers.filter((s) => {
            if (serverFilter !== "all" && String(s.server_port) !== serverFilter) return false;
            if (providerFilter !== "all" && s.provider_name !== providerFilter) return false;
            return true;
        });
    }, [servers, serverFilter, providerFilter]);

    const filteredUsers = useMemo(() => {
        return flatUsers.filter((u) => {
            if (serverFilter !== "all" && String(u.server_port) !== serverFilter) return false;
            if (providerFilter !== "all" && u.provider_name !== providerFilter) return false;
            if (monthFilter !== "all" && String(u.activation_date || "").slice(0, 7) !== monthFilter) return false;
            return true;
        });
    }, [flatUsers, serverFilter, providerFilter, monthFilter]);

    // Storage vs utilization with percentages
    const storageData = useMemo(() => {
        return filteredServers.map((s) => {
            const activeCount = filteredUsers.filter((u) => u.server_port === s.server_port && normalizeStatus(u.status) === "active").length;
            const totalStorage = parseToGB(s.rom);
            const usedStorage = activeCount; // 1 GB per active user
            const percentage = totalStorage > 0 ? Math.round((usedStorage / totalStorage) * 100) : 0;

            return {
                name: s.server_name || `Server ${s.server_port}`,
                totalStorage: totalStorage,
                usedStorage: usedStorage,
                freeStorage: Math.max(0, totalStorage - usedStorage),
                percentage: percentage,
                server_port: s.server_port
            };
        });
    }, [filteredServers, filteredUsers]);

    // Status pie with percentages
    const activeUsers = filteredUsers.filter((u) => normalizeStatus(u.status) === "active");
    const inactiveUsers = filteredUsers.filter((u) => normalizeStatus(u.status) !== "active");
    const totalUsers = activeUsers.length + inactiveUsers.length;

    const pieData = [
        { key: "active", name: `Active Users (${totalUsers > 0 ? Math.round((activeUsers.length / totalUsers) * 100) : 0}%)`, value: activeUsers.length },
        { key: "inactive", name: `Inactive Users (${totalUsers > 0 ? Math.round((inactiveUsers.length / totalUsers) * 100) : 0}%)`, value: inactiveUsers.length },
        { key: "servers", name: `Total Servers (${filteredServers.length})`, value: filteredServers.length },
    ];

    // Provider-wise data with percentages
    const providerLineData = useMemo(() => {
        const map = {};
        filteredUsers.forEach((u) => {
            const provider = u.provider_name || "Unknown";
            if (!map[provider]) map[provider] = { provider, active: 0, inactive: 0, total: 0 };
            if (normalizeStatus(u.status) === "active") map[provider].active += 1;
            else map[provider].inactive += 1;
            map[provider].total += 1;
        });
        return Object.values(map).map(p => ({
            ...p,
            activePercent: p.total > 0 ? Math.round((p.active / p.total) * 100) : 0,
            inactivePercent: p.total > 0 ? Math.round((p.inactive / p.total) * 100) : 0
        }));
    }, [filteredUsers]);

    // Provider storage usage with percentages
    const providerStorageData = useMemo(() => {
        const map = {};
        filteredServers.forEach((s) => {
            const provider = s.provider_name || "Unknown";
            if (!map[provider]) map[provider] = { provider, totalStorage: 0, usedStorage: 0, serverCount: 0 };
            const storage = parseToGB(s.rom);
            map[provider].totalStorage += storage;
            const activeCount = filteredUsers.filter((u) => u.server_port === s.server_port && normalizeStatus(u.status) === "active").length;
            map[provider].usedStorage += activeCount;
            map[provider].serverCount += 1;
        });
        return Object.values(map).map(p => ({
            ...p,
            freeStorage: Math.max(0, p.totalStorage - p.usedStorage),
            percentUsed: p.totalStorage > 0 ? Math.round((p.usedStorage / p.totalStorage) * 100) : 0
        }));
    }, [filteredServers, filteredUsers]);

    const handlePieClick = (entry) => {
        if (entry.key === "active") onShowGroup("Active Users", activeUsers);
        else if (entry.key === "inactive") onShowGroup("Inactive Users", inactiveUsers);
        else if (entry.key === "servers") onShowGroup("All Servers", filteredServers);
    };

    // Custom tooltip for storage data
    const CustomStorageTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            return (
                <div className="bg-white p-3 border border-slate-200 rounded-lg shadow-lg">
                    <p className="font-semibold text-slate-800">{label}</p>
                    <p className="text-sm text-slate-600">Total Storage: {data.totalStorage} GB</p>
                    <p className="text-sm text-slate-600">Used Storage: {data.usedStorage} GB</p>
                    <p className="text-sm text-slate-600">Free Storage: {data.freeStorage} GB</p>
                    <p className="text-sm font-semibold text-blue-600">Usage: {data.percentage}%</p>
                    <p className="text-xs text-slate-400">Server Port: {data.server_port}</p>
                </div>
            );
        }
        return null;
    };

    return (
        <div className="rounded-xl bg-white border border-slate-200 p-5" style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}>
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                <h2 className="text-sm font-bold text-slate-800">
                    Server & Usage Overview
                    {!isAdmin && <span className="ml-2 text-xs font-normal text-slate-500">(Employee View)</span>}
                    {isAdmin && <span className="ml-2 text-xs font-normal text-blue-600">(Admin View)</span>}
                </h2>
                <div className="flex gap-2 flex-wrap">
                    <select value={serverFilter} onChange={(e) => setServerFilter(e.target.value)} className="bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5 text-xs">
                        <option value="all">All Servers</option>
                        {servers.map((s) => (
                            <option key={s.server_port} value={String(s.server_port)}>{s.server_name || s.server_port}</option>
                        ))}
                    </select>
                    <select value={providerFilter} onChange={(e) => setProviderFilter(e.target.value)} className="bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5 text-xs">
                        <option value="all">All Providers</option>
                        {providerNames.map((p) => (
                            <option key={p} value={p}>{p}</option>
                        ))}
                    </select>
                    <select value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)} className="bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5 text-xs">
                        <option value="all">All Dates</option>
                        {monthOptions.map((m) => (
                            <option key={m} value={m}>{m}</option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Storage vs Utilization - Enhanced Bar Chart */}
                <div>
                    <h3 className="text-xs font-semibold text-slate-500 mb-2">Server Storage vs Used Storage</h3>
                    <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={storageData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                            <YAxis tick={{ fontSize: 10 }} label={{ value: 'GB', angle: -90, position: 'insideLeft', fontSize: 10 }} />
                            <Tooltip content={<CustomStorageTooltip />} />
                            <Legend />
                            <Bar dataKey="totalStorage" name="Total Storage (GB)" fill="#0369a1" radius={[4, 4, 0, 0]} />
                            <Bar dataKey="usedStorage" name="Used Storage (GB)" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                            <Bar dataKey="freeStorage" name="Free Storage (GB)" fill="#10b981" radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                    {/* Percentage labels */}
                    <div className="flex flex-wrap gap-2 mt-1">
                        {storageData.map((item) => (
                            <div key={item.server_port} className="text-[10px] bg-slate-50 px-2 py-0.5 rounded-full">
                                <span className="font-medium">{item.name.substring(0, 10)}</span>
                                <span className={`ml-1 ${item.percentage > 80 ? 'text-red-500' : item.percentage > 50 ? 'text-yellow-500' : 'text-green-500'}`}>
                                    {item.percentage}% used
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Pie Chart - Users & Servers Breakdown */}
                <div>
                    <h3 className="text-xs font-semibold text-slate-500 mb-2">Users & Servers Breakdown (click a slice)</h3>
                    <ResponsiveContainer width="100%" height={260}>
                        <PieChart>
                            <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={({ percent }) => `${(percent * 100).toFixed(0)}%`} onClick={handlePieClick} cursor="pointer">
                                {pieData.map((entry) => (
                                    <Cell key={entry.key} fill={PIE_COLORS[entry.key]} />
                                ))}
                            </Pie>
                            <Tooltip />
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                    <div className="text-center text-xs text-slate-500 mt-1">
                        Total Users: {totalUsers} | Total Servers: {filteredServers.length}
                        {totalUsers > 0 && ` | Active: ${Math.round((activeUsers.length / totalUsers) * 100)}%`}
                    </div>
                </div>

                {/* Provider-wise User Count with percentages */}
                <div>
                    <h3 className="text-xs font-semibold text-slate-500 mb-2">Provider-wise User Distribution</h3>
                    <ResponsiveContainer width="100%" height={260}>
                        <ComposedChart data={providerLineData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="provider" tick={{ fontSize: 10 }} />
                            <YAxis tick={{ fontSize: 10 }} />
                            <Tooltip formatter={(value, name) => {
                                if (name === 'Active %') return [`${value}%`, name];
                                if (name === 'Inactive %') return [`${value}%`, name];
                                return [value, name];
                            }} />
                            <Legend />
                            <Bar dataKey="active" name="Active Users" fill="#10b981" />
                            <Bar dataKey="inactive" name="Inactive Users" fill="#f43f5e" />
                            <Line type="monotone" dataKey="activePercent" name="Active %" stroke="#059669" strokeWidth={2} dot={{ r: 4 }} />
                            <Line type="monotone" dataKey="inactivePercent" name="Inactive %" stroke="#dc2626" strokeWidth={2} dot={{ r: 4 }} />
                        </ComposedChart>
                    </ResponsiveContainer>
                </div>

                {/* Provider Storage Usage with percentages */}
                <div>
                    <h3 className="text-xs font-semibold text-slate-500 mb-2">Provider Storage Usage</h3>
                    <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={providerStorageData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="provider" tick={{ fontSize: 10 }} />
                            <YAxis tick={{ fontSize: 10 }} label={{ value: 'GB', angle: -90, position: 'insideLeft', fontSize: 10 }} />
                            <Tooltip formatter={(value, name) => {
                                if (name === 'Total Storage' || name === 'Used Storage' || name === 'Free Storage') return [`${value} GB`, name];
                                if (name === 'Percent Used') return [`${value}%`, name];
                                return [value, name];
                            }} />
                            <Legend />
                            <Bar dataKey="totalStorage" name="Total Storage (GB)" fill="#0369a1" />
                            <Bar dataKey="usedStorage" name="Used Storage (GB)" fill="#f59e0b" />
                            <Bar dataKey="freeStorage" name="Free Storage (GB)" fill="#10b981" />
                        </BarChart>
                    </ResponsiveContainer>
                    {/* Provider percentage labels */}
                    <div className="flex flex-wrap gap-2 mt-1">
                        {providerStorageData.map((item) => (
                            <div key={item.provider} className="text-[10px] bg-slate-50 px-2 py-0.5 rounded-full">
                                <span className="font-medium">{item.provider.substring(0, 12)}</span>
                                <span className={`ml-1 ${item.percentUsed > 80 ? 'text-red-500' : item.percentUsed > 50 ? 'text-yellow-500' : 'text-green-500'}`}>
                                    {item.percentUsed}% used
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DashboardCharts;